import { type Association } from "@entities";
import { Typography } from "@mui/material";
import { useHyperTable } from "@shared/frontend/hyper-table/HyperTable";
import { NonEntityColumn } from "@shared/frontend/hyper-table/NonEntityColumn";
import { useMemo } from "react";
import {
  distanceMapFromMeta,
  formatRelevantVolunteerDistance,
  type RelevantVolunteersDistanceMeta,
} from "./relevant-volunteers-distance";

export function RelevantVolunteersDistanceColumn({
  distanceByUserId: distanceByUserIdProp,
  isRemoteOpportunity: isRemoteOpportunityProp,
}: {
  distanceByUserId?: Map<string, number | null>;
  isRemoteOpportunity?: boolean;
} = {}) {
  const ctx = useHyperTable<Association>();

  const relevantFilter = ctx.filters.find(
    (filter) => filter.id === "relevantVolunteers",
  );
  const filterMeta = relevantFilter?.meta as
    | RelevantVolunteersDistanceMeta
    | undefined;

  const distanceByUserId = useMemo(() => {
    if (distanceByUserIdProp) return distanceByUserIdProp;
    return distanceMapFromMeta(filterMeta) ?? new Map<string, number | null>();
  }, [distanceByUserIdProp, filterMeta]);

  const isRemoteOpportunity =
    isRemoteOpportunityProp ?? filterMeta?.isRemoteOpportunity === true;

  const hasCallerMap = distanceByUserIdProp !== undefined;
  if (!hasCallerMap && (!distanceByUserId || distanceByUserId.size === 0)) {
    return null;
  }

  return (
    <NonEntityColumn<Association>
      id="distanceFromOpportunity"
      size={120}
      minSize={110}
      enableSorting
      sortingFn={(rowA, rowB) => {
        const distanceA =
          distanceByUserId.get(rowA.original.userId) ??
          Number.POSITIVE_INFINITY;
        const distanceB =
          distanceByUserId.get(rowB.original.userId) ??
          Number.POSITIVE_INFINITY;
        return distanceA - distanceB;
      }}
      cell={({ row }) => (
        <Typography variant="body2" fontWeight={500}>
          {formatRelevantVolunteerDistance(
            distanceByUserId.get(row.original.userId),
            isRemoteOpportunity,
          )}
        </Typography>
      )}
      caption="מרחק מההתנדבות"
      deps={[distanceByUserId, isRemoteOpportunity]}
    />
  );
}
